import React, { useState, useEffect } from "react";
import Select from "react-select";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useGetYearConfigurationQuery } from "../../../redux/service/modules/admin";
import moment from "moment";

// Generate years for selection
const generateYears = (yearType) => {
  const now = moment();
  const currentYear = now.year();

  // For financial year, we need to adjust the years list
  // Financial year 2025-2026 should be labeled as 2026
  let startYear = currentYear;
  if (yearType === 'financial') {
    const currentMonth = now.month(); // 0-indexed (0 = January, 3 = April)
    // If we're in April or later, the current financial year ends in next year
    if (currentMonth >= 3) {
      startYear = currentYear + 1;
    }
  }

  return Array.from({ length: 50 }, (_, i) => ({
    value: startYear - i,
    label: startYear - i,
  }));
};

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    border: "none",
    width: "80px",
    padding: "0",
    minHeight: "25px!important",
    cursor: "pointer",
    boxShadow: "none",
    fontSize: "12px",
    background: "transparent",
  }),
  valueContainer: (provided) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    padding: "0 4px",
    justifyContent: "center",
    width: "80px",
  }),
  singleValue: (provided) => ({
    ...provided,
    fontWeight: "bold",
    margin: "0 auto",
    textAlign: "center",
    fontSize: "12px",
    color: "#333",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    display: "none", // Hide default dropdown icon
  }),
  menu: (provided) => ({
    ...provided,
    width: "80px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    borderRadius: "4px",
    marginLeft: 0, // Ensure menu aligns with the control
    marginTop: "4px", // Small gap between control and menu
    right: "auto", // Disable right alignment
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: "240px",
    padding: "0",
  }),
  option: (provided, { isFocused, isSelected }) => ({
    ...provided,
    backgroundColor: isSelected ? "#f9b3b6" : isFocused ? "#f9e3e4" : "white",
    color: "black",
    cursor: "pointer",
    padding: "8px",
    textAlign: "center",
    fontSize: "12px",
    fontWeight: isSelected ? "bold" : "normal",
    border: "none",
    borderBottom: "1px solid #f0f0f0",
  }),
  container: (provided) => ({
    ...provided,
    width: "80px",  // Ensure container width is consistent
    position: "static" // Use static positioning
  }),
};

const YearPicker = ({ onChange }) => {
  const { data: yearConfig } = useGetYearConfigurationQuery();
  const yearType = yearConfig?.year_type || 'calendar';
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  // Generate year options based on year type
  useEffect(() => {
    const yearOptions = generateYears(yearType);
    setYears(yearOptions);

    // Set default selected year (first in the list)
    if (yearOptions.length > 0 && !selectedYear) {
      setSelectedYear(yearOptions[0]);
      if (onChange) onChange(yearOptions[0].value);
    }
  }, [yearType, onChange]);

  const handleChange = (selected) => {
    setSelectedYear(selected);
    if (onChange) onChange(selected.value);
  };

  // Don't render until we have years
  if (!years.length || !selectedYear) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        height: "30px",
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        padding: "2px",
        paddingLeft: "12px",
        cursor: "pointer",
        backgroundColor: "white",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        position: "relative", // Needed for dropdown positioning
      }}
    >
      <CalendarTodayIcon style={{ fontSize: "1rem", color: "#555", marginRight: "4px" }} />
      <div style={{ position: "static", width: "80px" }}>
        <Select
          options={years}
          value={selectedYear}
          onChange={handleChange}
          styles={customStyles}
          classNamePrefix="year-selector"
          isSearchable={false}
          menuPlacement="bottom"
          menuPosition="absolute"
          menuShouldBlockScroll={false}
          menuShouldScrollIntoView={false}
        />
      </div>
    </div>
  );
};

export default YearPicker;
